<template>
  <vf-input
    ref="maskRef"
    v-model="maskedValue"
    :disabled
    :invalid
    :label="label || $t.zipCode"
    :required
    @change="onChange"
    @input="onInput"
  />
</template>

<script lang="ts" setup>
/**
 * This component expects the model value to be a string in any format.
 * It will emit the value transformed with a mask if provided via prop.
 * It will not provide any extra validation, just the input value formatting.
 */
const props = defineProps<{
  mask: string
  modelValue: string
  label?: string
  disabled?: boolean
  invalid?: boolean
  required?: boolean
  transforms?: string[]
}>()

const emit = defineEmits<{
  'update:modelValue': [payload: string]
  'change': [value: string]
}>()

defineSlots<{
  default?: () => void
}>()

const transformers = {
  capitalize: (value: string) => value.toUpperCase()
}

const maskRef = useTemplateRef('maskRef')
const maskFormat = computed(() => props.mask ? props.mask.replace(/#/g, 'X').replace(/A/g, 'X') : '')

const { value: maskedValue, el } = useMask(maskFormat.value)

function resolveNativeInputEl(): HTMLInputElement | undefined {
  const comp = maskRef.value as any
  const inputRef = comp?.inputRef
  if (!inputRef) return undefined

  const direct
    = (inputRef.el as HTMLInputElement | undefined)
      ?? (inputRef.$el as HTMLInputElement | undefined)

  if (direct && direct.tagName?.toLowerCase() === 'input') {
    return direct
  }

  const wrapper
    = (inputRef.el as HTMLElement | undefined)
      ?? (inputRef.$el as HTMLElement | undefined)
  if (!wrapper) return undefined

  return wrapper.querySelector('input') ?? undefined
}
onMounted(() => {
  // set initial value to trigger mask formatting
  const inputEl = resolveNativeInputEl()
  if (inputEl) {
    el.value = inputEl as unknown as HTMLInputElement
  }
  maskedValue.value = props.modelValue
})

// Temporary solution to emit 'change' event
// 'update:modelValue' should be emitted on 'input' event
const onChange = () => {
  emit('update:modelValue', maskedValue.value)
  emit('change', maskedValue.value)
}

/**
 * Passes value through transformer functions, configured in SFCC.
 * Example: 'capitalize' transformer used for CA postal codes (e.g. e5a 3n3 -> E5A 3N3).
 */
const onInput = (event) => {
  if (!event.target.value) return
  let newValue = event.target.value
  for (const transformer of props.transforms || []) {
    if (!transformers[transformer]) continue
    newValue = transformers[transformer](newValue)
  }
  maskedValue.value = newValue
}
</script>
